import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { ConfigProvider } from "antd";
import Illustration from "./components/Illustration";
import theme from "./config/theme";
import { getSession, homeFor } from "./services/session";

// Auth
import Login from "./screens/auth/Login";

// Layouts
import SuperAdminLayout from "./layouts/SuperAdminLayout";
import LabAdminLayout from "./layouts/LabAdminLayout";
import DoctorPortalRedirect from "./components/DoctorPortalRedirect";

// Super Admin Pages
import SuperAdminHome from "./screens/superadmin/Home";
import CkdRevenue from "./screens/superadmin/CkdRevenue";
import Users from "./screens/superadmin/Users";
import CreateUser from "./screens/superadmin/CreateUser";
import Doctors from "./screens/superadmin/Doctors";
import DoctorInspection from "./screens/superadmin/Doctors/DoctorInspection";
import DoctorHome from "./screens/doctor/Home";
import DoctorAppointmentsView from "./screens/doctor/Appointments/DoctorAppointmentsView";
import DoctorSettings from "./screens/doctor/Settings";
import Labs from "./screens/superadmin/Labs";
import LabAdmins from "./screens/superadmin/LabAdmins";
import LabTests from "./screens/lab/LabTests";
import Appointments from "./screens/superadmin/Appointments";
import Reports from "./screens/superadmin/Reports";

// Lab Admin Pages
import LabHome from "./screens/lab/Home";

import TestBookings from "./screens/lab/TestBookings";



// Doctor Pages
import LabPartners from "./screens/labpartners/LabPartners";
import PointsSettingsPage from "./screens/pointsSetting/PointsSettingsPage";
import HomeSettingsPage from "./screens/superadmin/homepagesetting/HomePageSetting";
import MembershipSettingsPage from "./screens/membershipSetting/MembershipSettingsPage";
import DoctorSettingsPage from "./screens/doctorSettings/DoctorSettingsPage";
import SuperAdminTestBookings from "./screens/superadmin/TestBookings";
import Policy from "./screens/superadmin/Policy";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const session = getSession();
  const { doctorId, labId } = useParams();
  if (!session || !allowedRoles.includes(session.user.role)) return <Navigate to={allowedRoles.includes("doctor") ? "/doctor/login" : "/login"} replace />;
  // UI guard only. The API must independently enforce account ownership.
  if (session.user.role !== 'superadmin' && (doctorId || labId) !== session.user._id) {
    return <Navigate to={homeFor(session.user)} replace />;
  }
  return children;
};

function App() {
  return (
    <ConfigProvider theme={theme.antd} renderEmpty={() => <div className="empty-art"><Illustration name="records" /><span>Nothing here yet</span></div>}>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/doctor/*" element={<DoctorPortalRedirect />} />
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* ⭐ SUPER ADMIN ROUTES */}
          <Route
            path="/superadmin"
            element={
              <ProtectedRoute allowedRoles={["superadmin"]}>
                <SuperAdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<SuperAdminHome />} />
            <Route path="users" element={<Users />} />
            <Route path="users/create" element={<CreateUser />} />
            <Route path="doctors" element={<Doctors />} />
            <Route path="doctors/create" element={<CreateUser />} />
            <Route path="doctors/:doctorId" element={<DoctorInspection />}>
              <Route index element={<DoctorHome />} />
              <Route path="appointments" element={<DoctorAppointmentsView />} />
              <Route path="settings" element={<DoctorSettings />} />
            </Route>
            <Route path="labs" element={<Labs />} />
            <Route path="lab-admins" element={<LabAdmins />} />
            <Route path="labs/create" element={<Labs />} />
            <Route path="lab-tests" element={<LabTests />} />
            <Route path="lab-tests/create" element={<LabTests />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="test-bookings" element={<SuperAdminTestBookings />} />
            <Route path="reports" element={<Reports />} />
            <Route path="ckd" element={<CkdRevenue />} />
            <Route path="reward-claims" element={<PointsSettingsPage claimsOnly />} />
            <Route path="pointsSetting" element={<PointsSettingsPage />} />
            <Route path="generalSetting" element={<HomeSettingsPage />} />
            <Route path="membershipSetting" element={<MembershipSettingsPage />} />
            <Route path="doctorSettings" element={<DoctorSettingsPage />} />
            <Route path="policy" element={<Policy />} />
          </Route>

          {/* 🏥 LAB ADMIN ROUTES */}
          <Route
            path="/lab/:labId"
            element={
              <ProtectedRoute allowedRoles={["labadmin", "superadmin"]}>
                <LabAdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<LabHome />} />
            <Route path="tests" element={<LabTests />} />
            <Route path="labTests" element={<LabTests />} />

            <Route path="tests/create" element={<LabTests />} />

            <Route path="labPartners" element={<LabPartners />} />

            <Route path="bookings" element={<TestBookings />} />

            {/* <Route path="settings" element={<LabSettings />} /> */}
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;
